import React from "react";

import type { KonvaEventObject } from "konva/lib/Node";
import { Group, Circle, Label, Tag, Text } from "react-konva";

import { KONVA_GROUPS, KONVA_SHAPES } from "../constants";
import { useAnnotationStore } from "../store/annotationStore";
import type { EditorAnnotation } from "../store/annotationTypes";

interface PointAnnotationProps {
  annotation: EditorAnnotation;
  screenPos: { x: number; y: number };
  isSelected: boolean;
  isAnnotating: boolean;
  isSelectionMode: boolean;
  dragBoundFunc: (pos: { x: number; y: number }) => { x: number; y: number };
  onSelect: () => void;
  onDragEnd: (e: KonvaEventObject<DragEvent>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Renders a point annotation with label and interactive controls
 */
export const PointAnnotation: React.FC<PointAnnotationProps> = ({
  annotation,
  screenPos,
  isSelected,
  isAnnotating,
  isSelectionMode,
  dragBoundFunc,
  onSelect,
  onDragEnd,
  onMouseEnter,
  onMouseLeave,
}) => {
  /**
   * Component-level hover subscription prevents parent re-renders
   */
  const isHovered = useAnnotationStore((state) => state.hoveredAnnotationId === annotation.id);
  return (
    <Group
      dragBoundFunc={dragBoundFunc}
      draggable={isAnnotating && isSelectionMode}
      key={annotation.id}
      name={KONVA_GROUPS.POINT_ANNOTATION}
      onClick={onSelect}
      onDragEnd={onDragEnd}
    >
      <Circle
        fill={isHovered || isSelected ? "rgba(245, 130, 16, 0.26)" : "#F58210"}
        id={annotation.id}
        name={KONVA_SHAPES.POINT_MARKER}
        perfectDrawEnabled={false}
        radius={isHovered ? 7 : 6}
        stroke={"#F58210"}
        strokeWidth={isSelected ? 4 : isHovered ? 2.5 : 2}
        x={screenPos.x}
        y={screenPos.y}
        onMouseEnter={(e) => {
          if (isAnnotating) {
            const stage = e.target.getStage();
            if (stage) {
              stage.container().style.cursor = "pointer";
            }
          }
          onMouseEnter?.();
        }}
        onMouseLeave={(e) => {
          if (isAnnotating) {
            const stage = e.target.getStage();
            if (stage) {
              stage.container().style.cursor = "default";
            }
          }
          onMouseLeave?.();
        }}
      />
      <Label
        id={`label-${annotation.id}`}
        name={KONVA_SHAPES.POINT_LABEL}
        x={screenPos.x}
        y={screenPos.y - 12}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Tag cornerRadius={3} fill="#F27D28" pointerDirection="down" pointerHeight={0} pointerWidth={0} />
        <Text align="center" fill="white" fontFamily="Inter" fontSize={14} padding={4} text={annotation.label} />
      </Label>
    </Group>
  );
};
